import React, { useState, useEffect } from "react";
import { AxiosError } from "axios";
import Scrollable from "composants/Scrollable";
import classNames from "classnames";

import { TabSatelliteProps } from "containers/satellites/SatellitesData";
import { Button } from "../button";
import { fetchCommentaireData, fetchDefaultComment, saveComment, deleteCommentaire } from "api";
import { Message } from "types/Message";
import { t } from "utils/i18n";
import { addMessage } from "actions/messages";
import { Pojo } from "types/Galaxy";
import TabHeader from "./TabHeader";
import { Dropdown, DropdownButton, DropdownMenu } from "../DropDown/Dropdown";
import EditeurCommentaire from "./EditeurCommentaire";
import DupplicationCommentaire from "./DupplicationCommentaire";
import { useInfiniteQuerySatellite } from "hooks/useInfiniteQuerySatellite";
import { COMMENTAIRES } from "constant/satellite";
import InfiniteList from "containers/listegenerique/InfiniteList";

import "./satelliteTab.css";
import "flag-icons/css/flag-icons.min.css";
import { Fa } from "composants/Icon";

export interface Langue extends Pojo {
  langCode: string;
  langLibelle: string;
}

export interface CommentaireType extends Pojo {
  cotyCode: string;
  cotyLibelle: string;
}

export interface Commentaire extends Pojo {
  langue: string;
  langueLibelle: string;
  type: string;
  typeLibelle: string;
  texte: string;
  dtSaisie: Date;
}

function TabCommentaire({
  contextId,
  countAction,
  tableName,
  sjmoCode,
  count,
  height
}: TabSatelliteProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [currentText, setCurrentText] = useState<string>("");
  const [currentLang, setCurrentLang] = useState<string | undefined>(undefined);
  const [currentType, setCurrentType] = useState<string | undefined>(undefined);
  const [defaultComment, setDefaultComment] = useState<Commentaire | undefined>(undefined);
  const [selectedComment, setSelectedComment] = useState<Commentaire | undefined>(undefined);
  const [forceUpdate, setForceUpdate] = useState(false);
  const loadMoreButtonRef = React.useRef<HTMLDivElement | null>(null);
  const isSavingRef = React.useRef<boolean>(false);

  const {
    data: commentaires,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuerySatellite(COMMENTAIRES, tableName, contextId, fetchCommentaireData);

  useEffect(() => {
    if (!contextId) return;

    // pré-record chargé une fois, réutilisé pour toutes les créations
    fetchDefaultComment(sjmoCode, tableName, contextId)
      .then(response => {
        setDefaultComment(response.data);
      })
      .catch(e => {
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });
  }, [contextId, sjmoCode, tableName]);

  // entrée/sortie du mode édition
  const handleEditing = (selected?: Commentaire) => {
    if (isEditing) {
      // Sortie du mode édition
      setIsEditing(false);
      setSelectedComment(undefined);
      setCurrentLang(undefined);
      setCurrentType(undefined);
      setCurrentText("");
    } else if (selected) {
      // Entré du mode édition avec un commentaire sélectionné
      setIsEditing(true);
      setSelectedComment(selected);
      setCurrentLang(selected.langue);
      setCurrentType(selected.type);
      setCurrentText(selected.texte);
    } else if (defaultComment) {
      // Entré du mode édition avec un pré-record
      setIsEditing(true);
      setCurrentLang(defaultComment.langue);
      setCurrentType(defaultComment.type);
      setCurrentText(defaultComment.texte ?? "");
    }
  };

  // entrée/sortie du mode duplication
  const handleDuplicating = (selected?: Commentaire) => {
    if (isDuplicating) {
      // Sortie du mode duplication
      setIsDuplicating(false);
      setSelectedComment(undefined);
      setCurrentLang(undefined);
      setCurrentType(undefined);
      setCurrentText("");
    } else if (selected) {
      // Entré du mode dupplication avec un commentaire sélectionné
      setIsDuplicating(true);
      setSelectedComment(selected);
      setCurrentLang(selected.langue);
      setCurrentType(selected.type);
      setCurrentText(selected.texte);
    } else if (defaultComment) {
      // Entré du mode dupplication avec un pré-record
      setIsDuplicating(true);
      setCurrentLang(defaultComment.langue);
      setCurrentType(defaultComment.type);
      setCurrentText(defaultComment.texte ?? "");
    }
  };

  const handleTransferring = (sourceText: string) => {
    // wrap en <div> : sourceText peut être du texte brut ou déjà du HTML
    const updatedSourceText = `<div>${sourceText}</div>`;
    setCurrentText(prev => prev + updatedSourceText);
    setForceUpdate(true);
  };

  const handleSaving = () => {
    if (isSavingRef.current) return;

    isSavingRef.current = true;

    const texte = currentText;
    saveComment({
      sjmoCode: sjmoCode,
      tableName: tableName,
      id: contextId as string,
      langue: currentLang as string,
      type: currentType as string,
      date: defaultComment ? defaultComment.dtSaisie : null,
      commentaireId: selectedComment ? selectedComment.id : null,
      texte: texte
    })
      .then(() => {
        isSavingRef.current = false;
        if (isEditing) {
          handleEditing();
        } else if (isDuplicating) {
          handleDuplicating();
        }
        refetch();
        countAction(tableName, contextId);
      })
      .catch(e => {
        isSavingRef.current = false;
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });
  };

  const handleDeleting = (id: string) => {
    return deleteCommentaire(sjmoCode, id)
      .then(() => {
        refetch();
        countAction(tableName, contextId);
      })
      .catch(e => {
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });
  };

  const buildCommentCard = (comment: Commentaire) => {
    // pas de drapeau pour "en" (langue, pas pays) -> on utilise "gb"
    let lang = comment.langueLibelle;
    if ("en" === comment.langueLibelle) lang = "gb";

    return (
      <article
        className={classNames(
          "message donnees-satellite-container",
          comment.type.includes("ALERT") ? "is-danger" : "is-info"
        )}
        key={comment.id}
      >
        <div className="message-body has-text-black">
          <div className="level commentaire-header">
            <p className="has-margin-bot">
              <span className={`fi fi-${lang}`} /> {comment.typeLibelle}
            </p>
            <span className="satellite-actions">
              <Button className="satellite-icon-btn" onClick={() => handleEditing(comment)}>
                <span className="icon">
                  <Fa icon="pencil" />
                </span>
              </Button>
              <Dropdown autoclose className="is-right">
                <DropdownButton
                  render={param => (
                    <Button
                      ref={param.buttonRef}
                      className="satellite-icon-btn is-danger-hover"
                      onClick={param.onOpen}
                    >
                      <span className="icon">
                        <Fa icon="trash" />
                      </span>
                    </Button>
                  )}
                />
                <DropdownMenu
                  isRight
                  render={param => (
                    <div className="dropdown-item">
                      <p>{t("commun_sur_de_suprimer")}</p>
                      <button
                        className="button is-danger is-fullwidth is-small"
                        onClick={() => {
                          handleDeleting(comment.id).then(param.onClose);
                        }}
                      >
                        {t("commun_oui")}
                      </button>
                    </div>
                  )}
                />
              </Dropdown>
              <Button className="satellite-icon-btn" onClick={() => handleDuplicating(comment)}>
                <span className="icon">
                  <Fa icon="file-import" />
                </span>
              </Button>
            </span>
          </div>
          <div className="content" dangerouslySetInnerHTML={{ __html: comment.texte }} />
        </div>
      </article>
    );
  };

  const handleChangingText = (text: string) => {
    setCurrentText(text);
    setForceUpdate(false);
  };

  return (
    <>
      <TabHeader
        i18nKey="commun_commentaires_liees"
        count={count}
        tableName={tableName}
        contextId={contextId}
        sjmoCode={sjmoCode}
      >
        <Button
          className={classNames("satellite-icon-btn", { "cursor-force-default": isDuplicating })}
          onClick={() => !isDuplicating && handleEditing()}
        >
          <span className="icon">
            <Fa icon="plus" />
          </span>
        </Button>
        <Button
          className={classNames("satellite-icon-btn", { "cursor-force-default": isEditing })}
          onClick={() => !isEditing && handleDuplicating()}
        >
          <span className="icon">
            <Fa icon="copy" />
          </span>
        </Button>
      </TabHeader>

      <Scrollable autoHide style={{ height }}>
        {isEditing ? (
          <EditeurCommentaire
            sjmoCode={sjmoCode}
            currentLang={currentLang as string}
            currentType={currentType as string}
            currentText={currentText}
            selectedComment={selectedComment}
            save={handleSaving}
            onChangeLang={setCurrentLang}
            onChangeType={setCurrentType}
            onChangeText={handleChangingText}
            editing={handleEditing}
            style={{ height: "35vh", overflow: "auto" }}
          />
        ) : isDuplicating ? (
          <DupplicationCommentaire
            sjmoCode={sjmoCode}
            currentLang={currentLang as string}
            currentType={currentType as string}
            currentText={currentText}
            selectedComment={selectedComment}
            save={handleSaving}
            onChangeLang={setCurrentLang}
            onChangeType={setCurrentType}
            onChangeText={handleChangingText}
            tableName={tableName}
            contextId={contextId as string}
            duplicating={handleDuplicating}
            transfert={handleTransferring}
            forceUpdate={forceUpdate}
          />
        ) : (
          <InfiniteList
            data={commentaires?.pages}
            loadMoreButtonRef={loadMoreButtonRef}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            renderCell={buildCommentCard}
            listHeight={height as number}
          />
        )}
      </Scrollable>
    </>
  );
}

export default TabCommentaire;
